import moment from 'moment'
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect } from 'react'
import { useQuery } from 'react-query'
import { TruckloadSMInventory } from 'src/api/models'
import { skuit } from 'src/services/skuit'
import { Barcode } from 'src/components/Barcode'

const useStyles = makeStyles(() => ({
  table: {
    width: '100%',
    margin: '0',
    padding: '0',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '.25em',
    fontSize: '.85em',
    fontWeight: 'bold',
  },
  tr: {
    borderTop: '1px solid gray',
    borderBottom: '1px solid gray',
    padding: '.25em',
    fontSize: '.8em',
  },
  td: {
    borderTop: '1px solid gray',
    borderBottom: '1px solid gray',
    padding: '.25em',
  },
  strong: {
    fontWeight: 'bold',
  },
  label: {
    maxWidth: '90%',
    margin: 'auto',
    border: '1px solid gray',
    borderRadius: '1rem',
    padding: '0.5rem',
  },
  tspaced: {
    marginTop: '1rem',
  },
  bspaced: {
    marginBottom: '1rem',
  },
}))

interface InventorySMProps {
  inventory: TruckloadSMInventory[]
}

const InventorySM = (props: InventorySMProps) => {
  const { inventory } = props
  const classes = useStyles()

  return (
    <>
      <table className={classes.table} aria-label="units table">
        <thead>
          <tr>
            <th className={classes.th}>SKU</th>
            <th className={classes.th}>Product</th>
            <th className={classes.th}>Number of Units</th>
            <th className={classes.th}>Total in Shipment</th>
            <th className={classes.th}>Ordered</th>
          </tr>
        </thead>
        <tbody>
          {inventory.map((row) => (
            <tr key={row.sku?.guid} className={`card-break ${classes.tr}`}>
              <td className={classes.td} align="right">
                {row.sku?.sku}
              </td>
              <td className={classes.td} align="right">
                {row.sku?.product?.name}
              </td>
              <td className={classes.td} align="right">
                {row.numUnits}
              </td>
              <td className={classes.td} align="right">
                {row.totalUnits}
              </td>
              <td className={classes.td} align="right">
                {row.numOrdered}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

interface ShipmentSMProps {
  guid: string
  onReadyToPrint?: () => void
}

export const PackingSlip = (props: ShipmentSMProps) => {
  const { guid, onReadyToPrint } = props
  const barcode = { barcode: guid, type: 'datamatrix', displayName: '' }
  const classes = useStyles()
  const { isLoading, data: shipment } = useQuery(['packing-slip', guid], () =>
    skuit.sc.parcel.shipments.packingSlip(guid)
  )

  useEffect(() => {
    if (!isLoading) {
      onReadyToPrint?.()
    }
  }, [isLoading, onReadyToPrint])

  if (isLoading) {
    return <div>Packing Slip not ready to print</div>
  }

  return (
    <div className={`always-page-break   ${classes.bspaced}`}>
      <div className="card-break">
        <Box px={5} pt={5}>
          <Grid container justify="space-between" alignItems="center" spacing={4}>
            <Grid item xs={4}>
              <Box>
                <Typography variant="h3">Packing Slip</Typography>
              </Box>
              <Box>
                <Typography variant="caption">Printed on {moment().format('lll')}</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell padding="none">
                      Order Number:
                    </TableCell>
                    <TableCell padding="none">
                      {shipment?.order && shipment.order.number}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none">
                      Tracking:
                    </TableCell>
                    <TableCell padding="none">
                      {shipment?.trackingNumber}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
            <Grid item xs={4}>
            </Grid>
            <Grid item xs={4} container>
              <Grid item xs={6}>
              </Grid>
              <Grid item xs={4}>
                <Barcode barcode={barcode} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">{shipment?.guid}</Typography>
              </Grid>
            </Grid>
            <Grid item xs={8}>
              <Box>
                <Typography variant="h5">Ship To</Typography>
              </Box>
              <Typography>{shipment?.consignee}</Typography>
              <Typography>{shipment?.address.address1}</Typography>
              {shipment?.address.address2 && <Typography>{shipment?.address.address2}</Typography>}
              <Typography>
                {shipment?.address.city}, {shipment?.address.state} {shipment?.address.postalCode}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <div className={`${classes.bspaced} ${classes.tspaced}`}>
                <Typography variant="h5">Summary</Typography>
                <Typography>{shipment?.totalUnits} Units</Typography>
              </div>
            </Grid>
          </Grid>
          <div className={`${classes.bspaced} ${classes.tspaced}`}>
            <Typography variant="h5">Inventory</Typography>
          </div>

          {!!shipment?.inventory?.length && (
            <>
              <InventorySM inventory={shipment?.inventory || []} />
            </>
          )}
        </Box>
      </div>

    </div>
  )
}
